import React, { useState } from 'react';
import { Order, ConvertOrderToCustomerRequest } from '../../types';
import { useCustomerStore } from '../../stores/customerStore';
import { useOrderStore } from '../../stores/orderStore';

interface ConvertOrderDialogProps {
  order: Order;
  open: boolean;
  onClose: () => void;
}

const ConvertOrderDialog: React.FC<ConvertOrderDialogProps> = ({ order, open, onClose }) => {
  const { convertFromOrder } = useCustomerStore();
  const { fetchOrders, fetchStats } = useOrderStore();
  
  const [formData, setFormData] = useState<ConvertOrderToCustomerRequest>({
    installation_date: new Date().toISOString().split('T')[0],
  });

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await convertFromOrder(order.id, formData);
      alert('Objednávka bola úspešne konvertovaná na zákazníka!');
      
      // Refresh orders to show updated status
      await fetchOrders();
      await fetchStats();
      
      onClose();
      
      // Reset form
      setFormData({
        installation_date: new Date().toISOString().split('T')[0],
      });
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Chyba pri konverzii objednávky');
    }
  };

  const handleChange = (field: keyof ConvertOrderToCustomerRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">🔄 Konvertovať objednávku na zákazníka</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Order Info */}
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <h3 className="font-semibold mb-2">Objednávka</h3>
            <div className="text-sm space-y-1">
              <div><strong>Číslo:</strong> {order.order_number}</div>
              <div>
                <strong>Zákazník:</strong>{' '}
                {order.customer_type === 'individual'
                  ? `${order.first_name} ${order.last_name}`
                  : order.company_name}
              </div>
              <div><strong>Email:</strong> {order.email}</div>
              <div><strong>Telefón:</strong> {order.phone}</div>
              <div><strong>Služba:</strong> {order.requested_speed || order.service_type}</div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Installation Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dátum inštalácie
              </label>
              <input
                type="date"
                value={formData.installation_date || ''}
                onChange={(e) => handleChange('installation_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Installation Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Poznámky k inštalácii
              </label>
              <textarea
                value={formData.installation_notes || ''}
                onChange={(e) => handleChange('installation_notes', e.target.value)}
                rows={3}
                placeholder="Poznámky technika z inštalácie..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Technical Details */}
            <div className="space-y-4">
              <h3 className="font-semibold">Technické údaje</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ONT Serial Number
                  </label>
                  <input
                    type="text"
                    value={formData.ont_serial || ''}
                    onChange={(e) => handleChange('ont_serial', e.target.value)}
                    placeholder="napr. HWTC12345678"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ONT MAC Address
                  </label>
                  <input
                    type="text"
                    value={formData.ont_mac || ''}
                    onChange={(e) => handleChange('ont_mac', e.target.value)}
                    placeholder="napr. 00:11:22:33:44:55"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    VLAN ID
                  </label>
                  <input
                    type="number"
                    value={formData.vlan_id || ''}
                    onChange={(e) => handleChange('vlan_id', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="napr. 100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IP Adresa
                  </label>
                  <input
                    type="text"
                    value={formData.ip_address || ''}
                    onChange={(e) => handleChange('ip_address', e.target.value)}
                    placeholder="napr. 192.168.1.100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stĺp ID
                  </label>
                  <input
                    type="number"
                    value={formData.pole_id || ''}
                    onChange={(e) => handleChange('pole_id', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="ID stĺpa v systéme"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Číslo portu
                  </label>
                  <input
                    type="text"
                    value={formData.port_number || ''}
                    onChange={(e) => handleChange('port_number', e.target.value)}
                    placeholder="napr. 1/1/1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>

            {/* Billing */}
            <div className="space-y-4">
              <h3 className="font-semibold">Fakturácia</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mesačný poplatok (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthly_fee || ''}
                    onChange={(e) => handleChange('monthly_fee', e.target.value ? parseFloat(e.target.value) : undefined)}
                    placeholder="napr. 15.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Deň fakturácie (1-31)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.billing_day || ''}
                    onChange={(e) => handleChange('billing_day', e.target.value ? parseInt(e.target.value) : undefined)}
                    placeholder="napr. 1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <div className="flex items-start">
                <span className="text-yellow-600 mr-2">ℹ️</span>
                <div className="text-sm text-yellow-800">
                  <strong>Poznámka:</strong> Po konverzii bude vytvorený nový zákazník s číslom CUST-YYYY-NNNN.
                  Objednávka bude označená ako "Konvertovaná" a nebude možné ju ďalej upravovať.
                  Zákazník bude v stave "Čaká na inštaláciu" a bude potrebné ho aktivovať po dokončení inštalácie.
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Zrušiť
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                🔄 Konvertovať na zákazníka
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConvertOrderDialog;

